import firebase from "firebase/compat/app"; // Import compat for Auth providers
import { db, auth, getSecondaryApp } from './firebase'; // Removed 'storage' import
import { 
  collection, getDocs, doc, setDoc, getDoc, deleteDoc, query, where, limit, writeBatch, updateDoc
} from "firebase/firestore";

import { User, UserRole, SurveyData, CardTemplate, DynamicQuestion, SurveySection } from '../types';
import { INITIAL_QUESTIONS, INITIAL_SECTIONS } from '../constants';

const SURVEYS_COLLECTION = 'surveys';
const USERS_COLLECTION = 'users';
const SETTINGS_COLLECTION = 'settings';

// Helper to construct email from username
export const getEmail = (username: string) => {
  const clean = username.trim();
  if (clean.includes('@')) return clean;
  return `${clean.toLowerCase().replace(/\s/g, '')}@mahallu.app`;
};

export const initStorage = async () => {
  // CRITICAL: If no user is logged in, Firestore rules will likely block this.
  // We check for currentUser. If null, we skip initialization.
  // It will be called again after login in App.tsx.
  if (!auth.currentUser) {
    console.log("Skipping storage initialization (Not logged in)");
    return;
  }

  try {
    // 1. Check Questions
    const questionsDoc = await getDoc(doc(db, SETTINGS_COLLECTION, 'questions'));
    if (!questionsDoc.exists()) {
      await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: INITIAL_QUESTIONS });
    } else {
      // CRITICAL FIX: Ensure mandatory system fields (SerialNo, HeadName, Phone) exist.
      let currentList = questionsDoc.data().list as DynamicQuestion[];
      const MANDATORY_IDS = ['serialNo', 'headName', 'houseName', 'wardNo', 'phone'];
      const LABEL_UPDATES: Record<string, string> = {
        'phone': 'മൊബൈൽ ഫോൺ നമ്പർ',
        'housePhoto': 'ഫോട്ടോ',
        'currentJob': 'തൊഴിൽ മേഖല'
      };
      
      let needsUpdate = false;
      
      // 1. Check Mandatory IDs
      MANDATORY_IDS.forEach(id => {
        const exists = currentList.find(q => q.id === id);
        if (!exists) {
          const original = INITIAL_QUESTIONS.find(q => q.id === id);
          if (original) {
            // Find appropriate section index to insert? Just unshift for now or push based on section
            currentList.unshift(original);
            needsUpdate = true;
          }
        }
      });

      // 2. Check Label Updates & Type Fixes (Phone should be text)
      currentList = currentList.map(q => {
        let modified = false;
        
        // Ensure Phone is text, not number
        if (q.id === 'phone' && q.type === 'number') {
           q.type = 'text';
           modified = true;
        }

        if (LABEL_UPDATES[q.id] && q.label !== LABEL_UPDATES[q.id]) {
          q.label = LABEL_UPDATES[q.id];
          modified = true;
        }
        
        if (modified) needsUpdate = true;
        return q;
      });

      if (needsUpdate) {
        console.log("Restoring missing mandatory questions, updating labels, and fixing types...");
        await updateDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: currentList });
      }
    }

    // 2. Check Sections
    const sectionsDoc = await getDoc(doc(db, SETTINGS_COLLECTION, 'sections'));
    if (!sectionsDoc.exists()) {
      await setDoc(doc(db, SETTINGS_COLLECTION, 'sections'), { list: INITIAL_SECTIONS });
    }
  } catch (error: any) {
    // Gracefully handle permission errors if rules are strict
    if (error.code === 'permission-denied') {
      console.warn("Storage initialization skipped due to permissions. (Normal if basic user)");
    } else {
      console.warn("Storage initialization warning:", error);
    }
  }
};

// --- Authentication ---

export const createUserProfile = async (uid: string, username: string, name: string, role: UserRole): Promise<User> => {
  const user: User = {
    id: uid,
    username,
    name,
    role,
    visibleSections: ['sectionA', 'sectionB', 'sectionC', 'sectionD'],
    isActive: true
  };
  await setDoc(doc(db, USERS_COLLECTION, uid), user);
  return user;
};

export const registerMasterAdmin = async (name: string, username: string, password: string): Promise<User> => {
  const email = getEmail(username);
  try {
    const userCredential = await auth.createUserWithEmailAndPassword(email, password);
    const uid = userCredential.user?.uid;
    
    if (!uid) throw new Error("Failed to retrieve User ID from Firebase.");

    return await createUserProfile(uid, username, name, UserRole.MASTER_ADMIN);
  } catch (error: any) {
    if (error.code === 'auth/email-already-in-use') {
      throw new Error(`The account '${email}' already exists.`);
    }
    if (error.code === 'auth/weak-password') {
      throw new Error("Password should be at least 6 characters.");
    }
    throw new Error(error.message || "Failed to register Master Admin.");
  }
};

export const loginUser = async (username: string, password: string): Promise<User> => {
  const email = getEmail(username);
  try {
    const userCredential = await auth.signInWithEmailAndPassword(email, password);
    const uid = userCredential.user?.uid;
    
    if (!uid) throw new Error("User ID not found after login");

    try {
      const userDoc = await getDoc(doc(db, USERS_COLLECTION, uid));
      if (userDoc.exists()) {
        const profile = { id: uid, ...userDoc.data() } as User;
        
        // Block Deactivated Users
        if (profile.isActive === false) {
           await auth.signOut();
           throw new Error("Your account has been deactivated. Please contact the administrator.");
        }
        
        return profile;
      } else {
        throw new Error("Account exists in Auth, but user profile is missing in Database.");
      }
    } catch (dbError: any) {
      if (dbError.message && dbError.message.includes("offline")) {
        throw new Error("Login successful, but cannot fetch profile (Offline). Check internet connection.");
      }
      throw dbError;
    }
  } catch (error: any) {
    const msg = error.message || '';
    if (msg.includes('auth/invalid-credential') || error.code === 'auth/wrong-password') {
      throw new Error(`Invalid credentials. Check username/password.`);
    }
    if (error.code === 'auth/network-request-failed') {
      throw new Error("Network error. Please check your connection.");
    }
    throw new Error(msg || "Login failed due to an unknown error.");
  }
};

export const logoutUser = async () => {
  await auth.signOut();
};

export const verifyUserCredentials = async (username: string, password: string) => {
  const currentUser = auth.currentUser;
  if (!currentUser || !currentUser.email) {
    throw new Error("No user is currently logged in.");
  }

  const inputEmail = getEmail(username);
  
  if (inputEmail.toLowerCase() !== currentUser.email.toLowerCase()) {
    throw new Error("Username does not match the currently logged-in user.");
  }

  try {
    const credential = firebase.auth.EmailAuthProvider.credential(currentUser.email, password);
    await currentUser.reauthenticateWithCredential(credential);
    return true;
  } catch (error: any) {
    if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      throw new Error("Incorrect password.");
    }
    throw error;
  }
};

// --- Users Management ---

export const getUsers = async (): Promise<User[]> => {
  try {
    const snapshot = await getDocs(collection(db, USERS_COLLECTION));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as User));
  } catch (error) {
    console.error("Failed to fetch users:", error);
    return [];
  }
};

export const saveUser = async (user: Omit<User, 'id'>, password: string) => {
  const secondaryApp = getSecondaryApp();
  const secondaryAuth = secondaryApp.auth();
  
  try {
    const email = getEmail(user.username);
    const userCredential = await secondaryAuth.createUserWithEmailAndPassword(email, password);
    const uid = userCredential.user?.uid;

    if (!uid) throw new Error("Failed to generate UID");

    await setDoc(doc(db, USERS_COLLECTION, uid), {
      username: user.username,
      name: user.name,
      role: user.role,
      isActive: true, // Default to true for new users
      visibleSections: user.visibleSections || ['sectionA', 'sectionB', 'sectionC', 'sectionD']
    });

    await secondaryAuth.signOut();
    await secondaryApp.delete();
  } catch (error: any) {
    console.error("Create User Error:", error);
    if (error.code === 'auth/email-already-in-use') {
      throw new Error("Username/Email already exists.");
    }
    throw new Error(error.message);
  }
};

export const updateUser = async (userId: string, data: Partial<User>) => {
  try {
    await updateDoc(doc(db, USERS_COLLECTION, userId), data);
  } catch (e: any) {
    throw new Error("Failed to update user profile: " + e.message);
  }
};

export const updateCurrentUserAuth = async (password: string) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Not logged in");
  if (password) await user.updatePassword(password);
};

export const deleteUser = async (userId: string) => {
  await deleteDoc(doc(db, USERS_COLLECTION, userId));
};

// --- Surveys ---

export const getSurveys = async (filters?: { userId?: string, limitCount?: number }): Promise<SurveyData[]> => {
  try {
    let q = query(collection(db, SURVEYS_COLLECTION));

    if (filters?.userId) {
      q = query(q, where('submittedBy', '==', filters.userId));
    }

    if (filters?.limitCount) {
      q = query(q, limit(filters.limitCount));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => d.data() as SurveyData);
  } catch (error) {
    console.error("Failed to fetch surveys:", error);
    return [];
  }
};

// Robust Serial Number Generation
// Uses a dedicated metadata document to track the last serial number
// to prevent race conditions and inconsistent gaps.
export const getNextSerialNo = async (): Promise<string> => {
  try {
    const metaRef = doc(db, SETTINGS_COLLECTION, 'metadata');
    const metaSnap = await getDoc(metaRef);

    if (metaSnap.exists() && typeof metaSnap.data().lastSerialNo === 'number') {
      const next = metaSnap.data().lastSerialNo + 1;
      return next.toString().padStart(4, '0');
    }

    // Fallback: If metadata doesn't exist (first run or migration), scan all surveys once
    console.log("Initializing Serial Number Counter from existing data...");
    const surveysSnapshot = await getDocs(collection(db, SURVEYS_COLLECTION));
    let max = 0;
    
    surveysSnapshot.docs.forEach(doc => {
      const s = doc.data() as SurveyData;
      const num = parseInt(s.sectionA?.serialNo || '0', 10);
      if (!isNaN(num) && num > max) max = num;
    });

    // Save this state so next time we don't need to scan
    await setDoc(metaRef, { lastSerialNo: max }, { merge: true });

    return (max + 1).toString().padStart(4, '0');
  } catch (e) {
    console.error("Serial generation failed:", e);
    // Emergency fallback to avoid crashing the form
    return '0001';
  }
};

// Utility to fix existing inconsistent data
export const repairSerialNumbers = async (): Promise<string> => {
  try {
    const snapshot = await getDocs(collection(db, SURVEYS_COLLECTION));
    const surveys = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as SurveyData));
    
    // Sort by submission date (oldest first)
    // We want to keep the ID of the oldest entry in case of duplicates
    surveys.sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime());

    const seenSerials = new Set<string>();
    const batch = writeBatch(db);
    let updatesCount = 0;
    
    // 1. Calculate the true maximum serial currently in existence
    let maxSerial = 0;
    surveys.forEach(s => {
       const currentStr = s.sectionA?.serialNo;
       const num = parseInt(currentStr || '0', 10);
       if (!isNaN(num) && num > maxSerial) maxSerial = num;
    });

    console.log("Current detected max serial:", maxSerial);

    // 2. Iterate and fix
    for (const survey of surveys) {
       const currentSerial = survey.sectionA?.serialNo;
       
       const isDuplicate = seenSerials.has(currentSerial);
       const isInvalid = !currentSerial || currentSerial === '0000' || isNaN(parseInt(currentSerial));

       if (isDuplicate || isInvalid) {
           // Assign new serial at the end of the chain
           maxSerial++;
           const newSerial = maxSerial.toString().padStart(4, '0');
           
           console.log(`Fixing survey ${survey.id}. Old: ${currentSerial}, New: ${newSerial}`);

           const docRef = doc(db, SURVEYS_COLLECTION, survey.id);
           
           // Construct safe update (preserving other Section A data if exists)
           const newSectionA = { ...(survey.sectionA || {}), serialNo: newSerial };
           batch.update(docRef, { sectionA: newSectionA });
           
           // Update our local map so we don't reuse this new serial
           seenSerials.add(newSerial);
           updatesCount++;
       } else {
           seenSerials.add(currentSerial);
       }
    }

    // 3. Update the global metadata counter to match the new max
    const metaRef = doc(db, SETTINGS_COLLECTION, 'metadata');
    
    if (updatesCount > 0) {
        batch.set(metaRef, { lastSerialNo: maxSerial }, { merge: true });
        await batch.commit();
        return `Repaired ${updatesCount} inconsistent serial numbers. Counter synced to ${maxSerial}.`;
    } else {
        // Just sync metadata if no survey updates needed
        await setDoc(metaRef, { lastSerialNo: maxSerial }, { merge: true });
        return `No duplicate serials found. Counter synced to ${maxSerial}.`;
    }

  } catch (error: any) {
     console.error("Repair error", error);
     throw new Error("Repair failed: " + error.message);
  }
};

export const saveSurvey = async (survey: SurveyData) => {
  let finalSurvey = { ...survey };
  
  // DUPLICATE CHECK & AUTO-CORRECTION
  // Before saving, ensure the serial number hasn't been taken by another user in the meantime.
  if (finalSurvey.sectionA?.serialNo) {
    const serialStr = finalSurvey.sectionA.serialNo;
    
    // Check if any OTHER survey has this serial number
    const q = query(
      collection(db, SURVEYS_COLLECTION), 
      where('sectionA.serialNo', '==', serialStr)
    );
    const snap = await getDocs(q);
    const isDuplicate = snap.docs.some(doc => doc.id !== finalSurvey.id);

    if (isDuplicate) {
      console.warn(`Duplicate Serial ${serialStr} detected. Auto-incrementing...`);
      // Force fetch the absolute latest next serial
      const nextSerial = await getNextSerialNo();
      // Update the payload
      finalSurvey.sectionA = { ...finalSurvey.sectionA, serialNo: nextSerial };
    }
  }

  // 1. Save the survey
  await setDoc(doc(db, SURVEYS_COLLECTION, finalSurvey.id), finalSurvey);

  // 2. Update the Global Counter
  // If the saved serial is higher than what's in metadata, update metadata.
  try {
    const currentSerial = parseInt(finalSurvey.sectionA?.serialNo || '0', 10);
    if (!isNaN(currentSerial) && currentSerial > 0) {
       const metaRef = doc(db, SETTINGS_COLLECTION, 'metadata');
       const metaSnap = await getDoc(metaRef);
       const storedMax = metaSnap.exists() ? (metaSnap.data().lastSerialNo || 0) : 0;

       if (currentSerial > storedMax) {
           await setDoc(metaRef, { lastSerialNo: currentSerial }, { merge: true });
       }
    }
  } catch (e) {
    console.error("Failed to update global serial counter", e);
  }
};

export const deleteSurvey = async (id: string) => {
  await deleteDoc(doc(db, SURVEYS_COLLECTION, id));
};

export const resetSurveys = async () => {
  const snapshot = await getDocs(collection(db, SURVEYS_COLLECTION));
  const batch = writeBatch(db);
  snapshot.docs.forEach((doc) => {
    batch.delete(doc.ref);
  });
  // Also reset metadata
  batch.set(doc(db, SETTINGS_COLLECTION, 'metadata'), { lastSerialNo: 0 });
  await batch.commit();
};

// --- Settings (Questions & Sections) ---

export const getDynamicSections = async (): Promise<SurveySection[]> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'sections'));
    if (docSnap.exists()) {
      return (docSnap.data().list as SurveySection[]).sort((a,b) => a.order - b.order);
    }
  } catch (error) {
    console.warn("Error fetching sections:", error);
  }
  return INITIAL_SECTIONS;
}

export const saveSections = async (sections: SurveySection[]) => {
  await setDoc(doc(db, SETTINGS_COLLECTION, 'sections'), { list: sections });
};

export const getDynamicQuestions = async (section?: string): Promise<DynamicQuestion[]> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'questions'));
    if (docSnap.exists()) {
      const all = docSnap.data().list as DynamicQuestion[];
      if (section) return all.filter(q => q.section === section);
      return all;
    }
  } catch (error) {
    // console.warn("Error fetching questions (likely permissions):", error);
    // Don't warn loudly, just return defaults.
  }
  return section ? INITIAL_QUESTIONS.filter(q => q.section === section) : INITIAL_QUESTIONS;
};

export const saveDynamicQuestion = async (q: DynamicQuestion) => {
  const current = await getDynamicQuestions();
  const index = current.findIndex(item => item.id === q.id);
  
  let newList = [...current];
  if (index >= 0) {
    newList[index] = q;
  } else {
    newList.push(q);
  }
  
  await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: newList });
};

export const saveQuestionsOrder = async (questions: DynamicQuestion[]) => {
  await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: questions });
};

export const deleteDynamicQuestion = async (id: string) => {
  const current = await getDynamicQuestions();
  const newList = current.filter(q => q.id !== id);
  await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: newList });
};

export const saveTemplate = async (template: CardTemplate) => {
  // We now save the Base64 string directly to the document
  // Firestore limit is 1MB, so image must be compressed client-side before calling this
  await setDoc(doc(db, SETTINGS_COLLECTION, 'template'), template);
};

export const getTemplate = async (): Promise<CardTemplate | null> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'template'));
    return docSnap.exists() ? docSnap.data() as CardTemplate : null;
  } catch (e) {
    console.warn("Failed to get template:", e);
    return null;
  }
};